
import i18next from 'i18next';

import { MHRecordingConstants } from '../base/lib-meet-hour';
import { getSoundFileSrc } from '../base/media/functions';
import { getRemoteParticipants } from '../base/participants/functions';
import { registerSound, unregisterSound } from '../base/sounds/actions';
import { isEmbedded } from '../base/util/embedUtils';
import { LIVE_STREAMING_OFF_SOUND_FILE, LIVE_STREAMING_ON_SOUND_FILE, RECORDING_OFF_SOUND_FILE, RECORDING_ON_SOUND_FILE } from '../recording/sounds';

import LocalRecordingManager from './components/Recording/LocalRecordingManager';
import { LIVE_STREAMING_OFF_SOUND_ID, LIVE_STREAMING_ON_SOUND_ID, RECORDING_OFF_SOUND_ID, RECORDING_ON_SOUND_ID, RECORDING_STATUS_PRIORITIES } from './constants';
import logger from './logger';


/**
 * Attaches a rejection handler to a lib-meet-hour recording promise.
 *
 * lib-meet-hour rejects these promises with the raw Strophe error stanza, which
 * is a cyclic XML structure. An unhandled rejection carrying such a value makes
 * React Native's rejection tracker throw "cyclical structure in JSON object"
 * from inside the native event emit path, so it must never escape. The reason
 * is deliberately reduced to a string before logging.
 *
 * @param {Promise|any} promise - The value returned by the lib call.
 * @param {string} label - Human readable description for the log message.
 * @returns {Promise<void>} Always resolves.
 */
function _swallowRecordingRejection(promise, label) {
    if (!promise || typeof promise.then !== 'function') {
        return Promise.resolve();
    }

    return promise.then(
        () => undefined,
        error => {
            let reason = 'unknown error';

            try {
                if (typeof error === 'string') {
                    reason = error;
                } else if (error && (error.message || error.name)) {
                    reason = error.message || error.name;
                } else if (error && typeof error.nodeType === 'number') {
                    reason = `error stanza <${error.tagName || error.nodeName || 'node'}>`;
                }
            } catch {
                reason = 'unserializable error';
            }

            logger.warn(`${label} failed: ${reason}`);
        });
}

/**
 * Calls {@code conference.startRecording()} and always swallows the rejection.
 *
 * @param {Object} conference - The conference to start the recording on.
 * @param {Object} options - The recording options passed to the lib.
 * @returns {Promise<void>} Always resolves.
 */
export function startRecordingSafely(conference: Object, options: Object) {
    if (!conference || typeof conference.startRecording !== 'function') {
        return Promise.resolve();
    }

    try {
        return _swallowRecordingRejection(conference.startRecording(options), 'startRecording');
    } catch (error) {
        logger.warn(`startRecording threw: ${error && error.message}`);

        return Promise.resolve();
    }
}

/**
 * Calls {@code conference.stopRecording()} and always swallows the rejection.
 *
 * {@code stopRecording()} returns a promise that rejects when the session is
 * unknown to the lib or when the server replies with an error stanza — both of
 * which are expected while we retry a stop during the PENDING phase. Leaving
 * those rejections unhandled trips React Native's unhandled-rejection tracker,
 * which then tries to {@code JSON.stringify()} the Strophe error stanza and
 * throws "cyclical structure in JSON object". Always attach a catch handler.
 *
 * @param {Object} conference - The conference to stop the recording on.
 * @param {string} sessionId - The ID of the recording session to stop.
 * @returns {Promise<void>} Always resolves.
 */
export function stopRecordingSafely(conference: Object, sessionId: string) {
    if (!conference || typeof conference.stopRecording !== 'function' || !sessionId) {
        return Promise.resolve();
    }

    try {
        return _swallowRecordingRejection(
            conference.stopRecording(sessionId),
            `stopRecording for session ${sessionId}`);
    } catch (error) {
        logger.warn(`stopRecording threw for session ${sessionId}: ${error && error.message}`);

        return Promise.resolve();
    }
}


/**
 * Checks whether a stop has already been requested for the given session ID but
 * the server has not confirmed it yet.
 *
 * @param {Object} state - The redux state to search in.
 * @param {string} id - The ID of the recording session to check.
 * @returns {boolean}
 */
function _isSessionStopping(state: Object, id: string): boolean {
    const { stoppingSessionIds } = state['features/recording'];

    return Boolean(stoppingSessionIds
        && typeof stoppingSessionIds.has === 'function'
        && stoppingSessionIds.has(id));
}

/**
 * Searches in the passed in redux state for an active recording session of the
 * passed in mode. Sessions for which a stop has already been requested are not
 * considered active, even if the server has not sent the OFF event yet.
 *
 * @param {Object} state - The redux state to search in.
 * @param {string} mode - Find an active recording session of the given mode.
 * @returns {Object|undefined}
 */
export function getActiveSession(state: Object, mode: string) {
    const { sessionDatas } = state['features/recording'];
    const { status: statusConstants } = MHRecordingConstants;

    return sessionDatas.find(sessionData => sessionData.mode === mode
        && !_isSessionStopping(state, sessionData.id)
        && (sessionData.status === statusConstants.ON
            || sessionData.status === statusConstants.PENDING));
}

/**
 * Returns an estimated recording duration based on the size of the video file
 * in MB. The estimate is calculated under the assumption that 1 min of recorded
 * video needs 10MB of storage on avarage.
 *
 * @param {number} size - The size in MB of the recorded video.
 * @returns {number} - The estimated duration in minutes.
 */
export function getRecordingDurationEstimation(size: ?number) {
    return Math.floor((size || 0) / 10);
}

/**
 * Searches in the passed in redux state for a recording session that matches
 * the passed in recording session ID.
 *
 * @param {Object} state - The redux state to search in.
 * @param {string} id - The ID of the recording session to find.
 * @returns {Object|undefined}
 */
export function getSessionById(state: Object, id: string) {
    return state['features/recording'].sessionDatas.find(
        sessionData => sessionData.id === id);
}

/**
 * Returns the recording session status that is to be shown in a label. E.g. If
 * there is a session with the status OFF and one with PENDING, then the PENDING
 * one will be shown, because that is likely more important for the user to see.
 * Sessions for which a stop has already been requested are ignored.
 *
 * @param {Object} state - The redux state to search in.
 * @param {string} mode - The recording mode to get status for.
 * @returns {string|undefined}
 */
export function getSessionStatusToShow(state: Object, mode: string): ?string {
    const recordingSessions = state['features/recording'].sessionDatas;
    let status;

    if (Array.isArray(recordingSessions)) {
        for (const session of recordingSessions) {
            if (session.mode === mode
                    && !_isSessionStopping(state, session.id)
                    && (!status
                        || (RECORDING_STATUS_PRIORITIES.indexOf(session.status)
                            > RECORDING_STATUS_PRIORITIES.indexOf(status)))) {
                status = session.status;
            }
        }
    }

    return status;
}


/**
 * Returns the resource id.
 *
 * @param {Object | string} recorder - A participant or it's resource.
 * @returns {string|undefined}
 */
export function getResourceId(recorder: string | Object) {
    if (recorder) {
        return typeof recorder === 'string'
            ? recorder
            : recorder.getId();
    }
}

/**
 * Unregisters the audio files based on locale.
 *
 * @param {Dispatch<any>} dispatch - The redux dispatch function.
 * @returns {void}
 */
export function unregisterRecordingAudioFiles(dispatch: any) {
    dispatch(unregisterSound(LIVE_STREAMING_OFF_SOUND_FILE));
    dispatch(unregisterSound(LIVE_STREAMING_ON_SOUND_FILE));
    dispatch(unregisterSound(RECORDING_OFF_SOUND_FILE));
    dispatch(unregisterSound(RECORDING_ON_SOUND_FILE));
}

/**
 * Registers the audio files based on locale.
 *
 * @param {Dispatch<any>} dispatch - The redux dispatch function.
 * @param {boolean|undefined} shouldUnregister - Whether the sounds should be unregistered.
 * @returns {void}
 */
export function registerRecordingAudioFiles(dispatch: any, shouldUnregister?: boolean) {
    const language = i18next.language;

    if (shouldUnregister) {
        unregisterRecordingAudioFiles(dispatch);
    }

    dispatch(registerSound(
        LIVE_STREAMING_OFF_SOUND_ID,
        getSoundFileSrc(LIVE_STREAMING_OFF_SOUND_FILE, language)));

    dispatch(registerSound(
        LIVE_STREAMING_ON_SOUND_ID,
        getSoundFileSrc(LIVE_STREAMING_ON_SOUND_FILE, language)));

    dispatch(registerSound(
        RECORDING_OFF_SOUND_ID,
        getSoundFileSrc(RECORDING_OFF_SOUND_FILE, language)));

    dispatch(registerSound(
        RECORDING_ON_SOUND_ID,
        getSoundFileSrc(RECORDING_ON_SOUND_FILE, language)));
}


/**
 * Whether a remote participant is recording locally or not.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean}
 */
export function isRemoteParticipantRecordingLocally(state) {
    const participants = getRemoteParticipants(state);


    for (const value of participants.values()) {
        if (value.localRecording) {
            return true;
        }
    }

    return false;
}


/**
 * Check if local recording is supported.
 *
 * @returns {boolean} - Whether local recording is supported or not.
 */
export function supportsLocalRecording() {
    return LocalRecordingManager.isSupported() && !isEmbedded();
}
